import axios from 'axios';
import {
  CosignerCreditScore,
  CreditScore,
  Degree, Lender,
  Quote, RateType, RepaymentPlan,
  Submission,
  University
} from "@/store/models";
import * as Toastify from 'toastify-js';

/**
 * Interface for the api config that represents various default values
 *
 * @interface Config
 * @property {string} baseURL - The base url to send all requests to
 * @property {string} contentType - The content type of your request
 * @property {string} accept - The data format that you will accept in the response
 * @property {string} authToken - Your JWT token received from signing in
 * */
interface Config {
    baseURL?: string;
    authToken?: string;
    contentType?: string;
    accept?: string;
    recaptcha?: string;
}

/**
 * Interface for the RequestAlert to be passed to the alert component
 *
 * @interface RequestAlert
 * @property {string} type - The base url to send all requests to
 * @property {string} title - The content type of your request
 * @property {string} message - The data format that you will accept in the response
 * */
interface RequestAlert {
  type: string;
  title: string;
  message: string;
}

/**
 * Generate an alert using Toastify
 *
 * @param {RequestAlert} alert
 * @returns void
 * */
function generateAlert(alert: RequestAlert) {
  const alertNode = document.createElement('div');
  const icon = alert.type === 'error' ?
    `<svg class="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>`
    : `<svg class="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>`
  alertNode.classList.add(
    'w-full', 'bg-white', 'shadow-lg', 'rounded-lg', 'pointer-events-auto'
  );
  alertNode.innerHTML = `<div class="rounded-lg shadow-xs overflow-hidden">
      <div class="p-4">
        <div class="flex items-start">
          ${ icon }
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm leading-5 font-medium text-gray-900">
              ${ alert.title }
            </p>
            <p class="mt-1 text-sm leading-5 text-gray-500">
              ${ alert.message }
            </p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button class="inline-flex text-gray-400 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150">
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>`;
  const toast = Toastify({
    node: alertNode,
    duration: 10000,
    gravity: "top", // `top` or `bottom`
    position: 'right', // `left`, `center` or `right`
    stopOnFocus: true, // Prevents dismissing of toast on hover
    onClick: () => {
      toast.hideToast();
    } // Callback after click
  }).showToast();
}

/**
 * Generic error handler
 *
 * @returns void
 * */
function errorHandler(error) {
  console.error(error);
  generateAlert({
    type: 'error',
    title: 'Oops, something went wrong!',
    message: 'Please try again or contact us at questions@joinjuno.com!',
  });
}

/**
 * Configure the API with some defaults
 *
 * @param {Config} config the config object
 * @returns void
 * */
export function configureAPI(config: Config) {
  if (config.baseURL) {
    axios.defaults.baseURL = config.baseURL;
  }
  if (config.authToken) {
    axios.defaults.headers.Authorization = `Bearer ${config.authToken}`;
  }
  if (config.contentType) {
    axios.defaults.headers['Content-Type'] = config.contentType;
  }
  if (config.recaptcha) {
    axios.defaults.params = {
      recaptcha: config.recaptcha
    };
  }
}

/**
 * Get all data
 *
 * @returns Promise
 * */
export function getAll() {
  return axios.get('/api/all')
    .then((response) => response)
    .catch(errorHandler);
}

/**
 * Get submissions
 *
 * @returns Promise
 * */
export function getSubmissions() {
  return axios.get('/api/submissions')
    .then((response) => response)
    .catch(errorHandler);
}

/**
 * Set submissions
 *
 * @returns Promise
 * */
export function setSubmission(submission: Submission, quotes: Array<Quote>) {
  return axios.post('/api/submissions', {
    submission,
    quotes,
  })
    .then((response) => {
      generateAlert({
        type: 'success',
        title: `Thank you for submitting your quote${quotes.length > 1 ? 's' : ''}!`,
        message: `We will publish the quote${quotes.length > 1 ? 's' : ''} shortly.`,
      });
      return response;
    })
    .catch(errorHandler);
}

/**
 * Upload a file for a submission
 *
 * @returns Promise
 * */
export function uploadFileForQuotes(id, quotes) {
  const formData = new FormData();
  quotes.forEach((quote) => {
    formData.append(quote.id, quote.file);
  });

  return axios(`/api/submissions/${id}/file`, {
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
      accept: 'application/json',
      processData: false,
      contentType: false,
    },
    data: formData,
  })
    .then((response) => response)
    .catch((error) => {
      console.error(error);
    });
}

/**
 * Interface for any quote related data
 *
 * @interface QuoteRequest
 * */
interface QuoteRequest {
  hasCosigner: boolean,
  creditScores: Array<CreditScore>,
  cosignerCreditScores: Array<CosignerCreditScore>,
  universities: Array<University>,
  degreeTypes: Array<string>,
  rateTypes: Array<RateType>,
  repaymentPlans: Array<RepaymentPlan>,
  lenders: Array<Lender>,
}

/**
 * Get histogram data
 *
 * @param {QuoteRequest} data
 * @returns Promise
 * */
export function getHistogramData(data: QuoteRequest) {
  return axios.post('/api/quotes/histogram', data)
    .then((response) => response)
    .catch(errorHandler);
}

/**
 * Get submissions
 *
 * @returns Promise
 * */
export function getRecommendations(data: QuoteRequest) {
  return axios.post('/api/quotes/recommendations', data)
    .then((response) => response)
    .catch(errorHandler);
}

